import { useState, useEffect } from "react";
import { Table, Typography, Space, Input, Badge, Tooltip, Button, Popconfirm, message, Image, Tag } from "antd";
import { SearchOutlined, PlusOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import axios from "axios";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";
import categoriesQuery from "./queries/categoriesQuery";
import deleteCategoryMutation from "./queries/deleteCategoryMutation";
import ParseLogo from "../shared/images/parse.png";
import CreateCategoryModal from "./CreateCategoryModal";
import EditCategoryModal from "./EditCategoryModal";

const { Title } = Typography;
const { Search } = Input;

const MarketplaceCategories = () => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  const fetchCategories = async (page = 1, pageSize = 20, search = "") => {
    setLoading(true);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      const where = {};
      if (search) {
        where.name = { matchesRegex: search, options: "i" };
      }

      const variables = {
        first: pageSize,
        skip: (page - 1) * pageSize,
        where: Object.keys(where).length > 0 ? where : undefined,
        order: ["name_ASC"],
      };

      const response = await axios.post(
        graphqlUrl,
        {
          query: categoriesQuery,
          variables,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const edges = response.data?.marketplace_categories?.edges || [];
      const count = response.data?.marketplace_categories?.count || 0;

      setCategories(edges.map((edge) => edge.node));
      setPagination((prev) => ({ ...prev, current: page, pageSize, total: count }));
    } catch (error) {
      console.error("Error fetching marketplace categories:", error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories(pagination.current, pagination.pageSize, searchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (newPagination) => {
    fetchCategories(newPagination.current, newPagination.pageSize, searchValue);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchCategories(1, pagination.pageSize, value);
  };

  const handleCreateCategory = () => {
    setCreateModalVisible(false);
    // Refresh the list after creating
    fetchCategories(1, pagination.pageSize, searchValue);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setEditModalVisible(true);
  };

  const handleUpdateCategory = () => {
    setEditModalVisible(false);
    setSelectedCategory(null);
    // Refresh the list after updating
    fetchCategories(pagination.current, pagination.pageSize, searchValue);
  };

  const handleDelete = async (categoryId) => {
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      await axios.post(
        graphqlUrl,
        {
          query: deleteCategoryMutation,
          variables: { id: categoryId },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success("Categoria eliminata con successo");
      // Refresh the list
      fetchCategories(pagination.current, pagination.pageSize, searchValue);
    } catch (error) {
      console.error("Error deleting category:", error);
      message.error("Errore durante l'eliminazione della categoria");
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "objectId",
      key: "objectId",
      width: 100,
    },
    {
      title: "Nome",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Modalità",
      key: "modality",
      render: (_, record) => (
        <Space size="small">
          {record.b2b && <Tag color="blue">B2B</Tag>}
          {record.b2c && <Tag color="green">B2C</Tag>}
        </Space>
      ),
    },
    {
      title: "Icona",
      dataIndex: "icon",
      key: "icon",
      render: (icon) =>
        icon?.url ? (
          <Image
            src={icon.url}
            alt={icon.name}
            width={40}
            height={40}
            style={{ objectFit: "cover" }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Copertina",
      dataIndex: "cover",
      key: "cover",
      render: (cover) =>
        cover?.url ? (
          <Image
            src={cover.url}
            alt={cover.name}
            width={60}
            height={40}
            style={{ objectFit: "cover" }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Data Creazione",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString("it-IT"),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Azioni",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditCategory(record)}
          >
            Modifica
          </Button>
          <Popconfirm
            title="Sei sicuro di voler eliminare questa categoria?"
            onConfirm={() => handleDelete(record.objectId)}
            okText="Si"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Elimina
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
    {
      title: "Links",
      key: "links",
      render: (_, record) => {
        const parseDashboardUrl = `${config.parseDashboard[isProd ? "prod" : "dev"]}/browser/marketplace_category?filters=[{"field":"objectId","constraint":"eq","compareTo":"${record.objectId}"}]`;
        return (
          <Tooltip title="Visualizza in Parse Dashboard" placement="top">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={parseDashboardUrl}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                backgroundColor: 'transparent',
                transition: 'all 0.2s ease',
              }}
            >
              <img
                src={ParseLogo}
                width={20}
                alt="Parse Dashboard"
                style={{ display: 'block' }}
              />
            </a>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div style={{ margin: "16px 16px" }}>
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={3} style={{ margin: 0 }}>
              <Space>
                Categorie Marketplace
                <Badge overflowCount={5000} count={pagination.total} />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateModalVisible(true)}
                >
                  Crea Categoria
                </Button>
              </Space>
            </Title>
            <Search
              placeholder="Cerca per nome"
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={handleSearch}
              style={{ width: 400 }}
            />
          </div>
        </Space>
      </div>

      <Table
        dataSource={categories}
        columns={columns}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        rowKey="objectId"
        scroll={{ x: "max-content" }}
      />

      <CreateCategoryModal
        visible={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onCreate={handleCreateCategory}
      />

      <EditCategoryModal
        visible={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedCategory(null);
        }}
        onUpdate={handleUpdateCategory}
        category={selectedCategory}
      />
    </div>
  );
};

export default MarketplaceCategories;
